// @flow

import styled from '@emotion/styled'
import transparentize from 'polished/lib/color/transparentize'
import * as React from 'react'
import Modal from 'react-modal'

import { colors, media } from '@toggl/style'

import { IconPlay } from '../icons/IconPlay'

type Props = {
  children?: React.Node,
  className?: string,
  Content?: React.ComponentType<any>,
  placeholder?: string | React.Node,
  skewed?: boolean,
  cta?: React.Node,
}

export const SectionModalVideo = ({
  children,
  Content,
  placeholder,
  skewed,
  cta,
  className,
  ...props
}: Props) => {
  const [isModalOpen, setModalOpen] = React.useState(false)

  function openModal() {
    setModalOpen(true)
  }

  function closeModal() {
    setModalOpen(false)
  }

  const ctaContent = children
    ? cta || (
        <IconPlay color={colors.darkPurple100} arrowColor={colors.peach100} />
      )
    : null

  return (
    <Root {...props} className={className}>
      <div>
        {skewed ? (
          <SkewedImgWrapper
            hasChildren={!!children}
            onClick={children && openModal}
          >
            {placeholder}
            {ctaContent}
          </SkewedImgWrapper>
        ) : (
          <ImgWrapper onClick={children && openModal} hasChildren={!!children}>
            {placeholder}
            {ctaContent}
          </ImgWrapper>
        )}

        {Content !== undefined && <Content />}
      </div>

      {children && (
        <StyledModal
          style={customStyles}
          isOpen={isModalOpen}
          onRequestClose={closeModal}
        >
          {children}

          <CloseOption
            onClick={() => {
              closeModal()
            }}
          >
            Close
          </CloseOption>
        </StyledModal>
      )}
    </Root>
  )
}

const Root: React.ComponentType<any> = styled.div`
  video,
  div > div > img,
  .gatsby-image-wrapper {
    height: auto;
    width: 100%;
    max-width: 320px;
    ${media.mq[1]} {
      max-width: none;
    }
  }
  svg {
    cursor: pointer;
  }
`

const StyledModal = styled(Modal)`
  inset: 50px 10px 0 !important;
  ${media.mq[1]} {
    inset: 50px !important;
  }
`

export const Img: React.ComponentType<any> = styled.img`
  width: 260px;

  ${media.mq[1]} {
    width: 388px;
  }
`

const ImgWrapper = styled.div`
  position: relative;
  ${(p) => (p.hasChildren ? 'cursor: pointer;' : '')}

  svg {
    fill: ${colors.peach100};
    position: absolute;
    width: 48px;
    height: 48px;
    top: calc(50% - 24px);
    left: calc(50% - 24px);
  }

  ${media.mq[1]} {
    svg {
      width: 62px;
      height: 62px;
      top: calc(50% - 31px);
      left: calc(50% - 31px);
    }
  }
`

const CloseOption = styled.span`
  position: fixed;
  top: 8px;
  right: 8px;
  padding: 10px;
  cursor: pointer;
  color: ${colors.peach100};
  &:hover {
    color: ${(p) => p.theme.primary};
  }
`

const customStyles = {
  /* stylelint-disable selector-type-no-unknown */
  overlay: {
    background: transparentize(0.3, colors.darkPurple90),
    overflow: 'hidden',
    height: '100%',
    zIndex: '10000',
  },
  content: {
    position: 'absolute',
    top: '50px',
    left: '80px',
    right: '80px',
    bottom: '50px',
    padding: '0',
    border: 'none',
    background: 'transparent',
    overflow: 'hidden',
  },
  /* stylelint-enable */
}

const SkewedImgWrapper = styled.div`
  position: relative;
  text-align: center;
  margin-top: 50px;
  svg {
    width: 64px;
    height: 64px;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    margin: auto;
    z-index: 110;
    background: ${colors.darkPurple100};
    border: 3px solid ${colors.peach100};
    border-radius: 50%;
    cursor: pointer;
    transition: 0.35s ease-in-out;
    opacity: 0.9;
    &:hover {
      transform: scale(1.15);
      opacity: 1;
    }
    ${(p) => (p.hasChildren ? 'cursor: pointer;' : '')}
  }

  > img,
  .gatsby-image-wrapper {
    transform: rotate(10deg);
    position: relative;
    z-index: 105;
    ${media.mq[1]} {
      margin-right: 20px;
    }
  }

  ${media.mq[1]} {
    margin-top: 0;
    svg {
      width: 95px;
      height: 95px;
    }
  }
`
